<template>
  <div class="relative z-20 flex items-center gap-3">
    <button
      type="button"
      class="relative grid h-9 w-9 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
      @click="toggleNotifications">
      <i class="far fa-bell"></i>
      <span v-if="notifications.length > 0" class="absolute right-1 top-1 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[10px] font-semibold leading-[18px] text-white">
        {{ notifications.length > 9 ? '9+' : notifications.length }}
      </span>
    </button>

    <button
      type="button"
      class="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-2 py-1.5 transition hover:border-slate-300 hover:bg-slate-50"
      @click="toggleMenu">
      <div class="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-[#0b78d1] text-xs font-semibold text-white">
        <img
          v-if="user?.profileImage"
          :src="user.profileImage"
          alt="Foto do usuário"
          class="h-full w-full object-cover" />
        <span v-else>{{ userInitials }}</span>
      </div>

      <div class="hidden text-left sm:block">
        <div class="text-sm font-semibold leading-tight text-slate-800">{{ user?.name || 'Usuário' }}</div>
        <div class="text-[11px] leading-tight text-slate-500">{{ user?.email || 'Sem e-mail' }}</div>
      </div>

      <i class="fa-solid fa-chevron-down text-[11px] text-slate-500 transition" :class="{ 'rotate-180': menuOpen }"></i>
    </button>

    <TransitionX>
      <Notification
        v-if="!hideNotifications"
        :notifications="notifications"
        @openNotification="openNotification"
        @readNotification="readNotification"
        @openHistory="openNotificationHistory" />
    </TransitionX>

    <TransitionX>
      <Card class="absolute right-0 top-full mt-2 w-[220px] p-2 shadow-lg" v-if="menuOpen">
        <div class="space-y-1">
          <button
            type="button"
            class="flex w-full items-center rounded-[8px] px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
            @click="goToProfile">
            <i class="fa-regular fa-user mr-3 text-[13px]"></i>
            Meu perfil
          </button>

          <button
            type="button"
            class="flex w-full items-center rounded-[8px] px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
            @click="logout">
            <i class="fa-solid fa-arrow-right-from-bracket mr-3 text-[13px]"></i>
            Sair
          </button>
        </div>
      </Card>
    </TransitionX>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import axios from 'axios'
import { get, update } from '@/crud'
import { baseApiUrl } from '@/global'

const store = useStore()
const router = useRouter()

const user = computed(() => store.state.user)
const userInitials = computed(() => {
  const source = String(user.value?.name || '')
  return (
    source
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'SS'
  )
})

const hideNotifications = ref(true)
const menuOpen = ref(false)
const notifications = ref([])
const route = 'notification'
let notificationsPollHandle = null

const getNotifications = async () => {
  if (!user.value) return

  const response = await get(route, { params: { limit: 20 } })
  notifications.value = response.data || []
}

const toggleNotifications = async () => {
  hideNotifications.value = !hideNotifications.value
  menuOpen.value = false

  if (!hideNotifications.value) {
    await getNotifications()
  }
}

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
  hideNotifications.value = true
}

const readNotification = async (notification) => {
  const id = notification?.id
  if (!id) return

  await update(route, id, {}, null)
  notifications.value = notifications.value.filter((item) => item.id !== id)
  await getNotifications()
}

const openNotification = async (notification) => {
  await readNotification(notification)
  hideNotifications.value = true

  if (notification?.routePath) {
    router.push(notification.routePath)
  }
}

const openNotificationHistory = () => {
  hideNotifications.value = true
  router.push('/administracao/notificacoes')
}

const startNotificationsPolling = () => {
  if (notificationsPollHandle) clearInterval(notificationsPollHandle)
  notificationsPollHandle = setInterval(() => {
    void getNotifications()
  }, 60000)
}

const goToProfile = () => {
  menuOpen.value = false
  router.push('/meu_perfil')
}

const logout = async () => {
  const url = `${baseApiUrl}/logout`
  axios.defaults.withCredentials = true
  localStorage.clear()

  try {
    await axios.post(url)
    localStorage.setItem('user', null)
    store.dispatch('setUser', null)
    router.push('/login')
  } catch (err) {
    console.log(err)
    return err
  }
}

onMounted(async () => {
  await getNotifications()
  startNotificationsPolling()
})

onBeforeUnmount(() => {
  if (notificationsPollHandle) clearInterval(notificationsPollHandle)
  notificationsPollHandle = null
})
</script>
